/**
 * Recovery for hashed page chunks that disappear after a deploy.
 *
 * Each release serves its own `public/build/assets/*`, so a chunk URL held by
 * an already-loaded client can 404 once the web root moves to a new release —
 * returning the HTML error page instead of JavaScript. The dynamic import then
 * fails with "Importing a module script failed" (WebKit) or "Failed to fetch
 * dynamically imported module" (Chromium), leaving the page dead.
 *
 * Webviews are the worst case: they run inside the mobile app, so the user has
 * no way to hard-refresh and must kill the app to recover. Reloading re-fetches
 * the document and picks up the current release's chunk names.
 *
 * This is the last line of defence, not the fix. Chunk names are content-hashed
 * (see vite.config.js) so unchanged chunks survive a deploy, and the deploy
 * should keep the previous release's assets reachable. This handles what still
 * slips through.
 */

const RELOAD_FLAG = "staleChunkReload";

/**
 * Whether a rejected dynamic import looks like a missing chunk rather than a
 * genuine error thrown inside the module.
 *
 * Deliberately broad: a needless reload costs a second, while a missed one
 * leaves the user on a blank page.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
const isStaleChunkError = (error) => {
    const message = String(error?.message ?? error ?? "");

    return (
        message.includes("Importing a module script failed") ||
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Importing binding name")
    );
};

/**
 * Reload once to recover from a stale chunk.
 *
 * The session-scoped flag is what keeps a genuinely broken build from becoming
 * a reload loop: a second failure in the same session rethrows and surfaces in
 * Sentry exactly as it does today.
 *
 * @param {unknown} error
 * @returns {Promise<never>} A promise that never settles, so the caller stops
 *  rendering while the reload is in flight.
 * @throws {unknown} The original error when a reload has already been tried, or
 *  when the failure is not chunk-related.
 */
const reloadOnceForStaleChunk = (error) => {
    if (!isStaleChunkError(error) || sessionStorage.getItem(RELOAD_FLAG)) {
        throw error;
    }

    sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();

    // Hold the render: the document is being replaced.
    return new Promise(() => {});
};

/**
 * Clear the guard once a page resolves, so a later deploy in the same session
 * can recover again.
 */
const clearStaleChunkGuard = () => {
    sessionStorage.removeItem(RELOAD_FLAG);
};

export {
    RELOAD_FLAG,
    isStaleChunkError,
    reloadOnceForStaleChunk,
    clearStaleChunkGuard,
};
